import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import colors from "../../../../theme/Colours";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  StyledSalesAnalyticsCard,
  SalesAnalyticsOverflowBox,
} from "../styles";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type PackSizeData = {
  type: string;
  vol: string | number;
  unit: string;
  onhover_unit: string;
};

type PeriodData = {
  period: "current" | "one" | "two" | string;
  name: string;
  values: PackSizeData[];
};

const normalizeLabel = (t: string) => t?.replace(" Kegs", "L Kegs");

const extractLitres = (onhoverUnit: string): number => {
  const match = onhoverUnit.match(/(\d+)\s*litres/);
  return match ? parseInt(match[1]) : 0;
};

const extractCapacity = (type: string): number => {
  const match = type.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
};

const displayElementsAndOrder = ["20 Kegs", "30 Kegs", "50 Kegs"];

export default function SalesByPackSize({
  comparisonFilterEnabled,
  data,
  periodValues,
}: {
  comparisonFilterEnabled?: boolean;
  data: {
    key: string;
    name: string;
    unit: string;
    details: PeriodData[];
  };
  periodValues: any;
}) {
  const periodData = useMemo(() => {
    const details = data?.details || [];
    const period1 = details.find((d) => d.period === "one")?.values || [];
    const period2 = details.find((d) => d.period === "two")?.values || [];
    const current = details.find((d) => d.period === "current")?.values || [];

    const createMap = (arr: PackSizeData[]) => ({
      litres: new Map(arr.map((v) => [v.type, extractLitres(v.onhover_unit)])),
      types: arr.map((v) => v.type),
      original: new Map(arr.map((v) => [v.type, v])),
    });

    return {
      period1: createMap(period1),
      period2: createMap(period2),
      current: createMap(current),
    };
  }, [data]);

  const { labels, types } = useMemo(() => {
    const unionTypes = Array.from(
      new Set([...periodData.current.types, ...displayElementsAndOrder])
    );

    const sortedTypes = unionTypes.sort((a, b) => {
      return (
        displayElementsAndOrder.indexOf(a) - displayElementsAndOrder.indexOf(b)
      );
    });

    return {
      labels: sortedTypes.map(normalizeLabel),
      types: sortedTypes,
    };
  }, [comparisonFilterEnabled, periodData]);

  const chartData = useMemo(() => {
    if (comparisonFilterEnabled) {
      const { period1, period2 } = periodData;
      return {
        labels,
        datasets: [
          {
            label: "Period 1",
            data: types.map((t) => period1.litres.get(t) ?? 0),
            backgroundColor: colors.linePlotBlue,
            borderRadius: 4,
            barThickness: 20,
          },
          {
            label: "Period 2",
            data: types.map((t) => period2.litres.get(t) ?? 0),
            backgroundColor: colors.barPlotLightBlue,
            borderRadius: 4,
            barThickness: 20,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: "Current sales",
          data: types.map((t) => periodData.current.litres.get(t) ?? 0),
          backgroundColor: colors.barPlotOrange,
          borderRadius: 4,
          barThickness: 30,
        },
      ],
    };
  }, [comparisonFilterEnabled, labels, types, periodData]);

  const options = useMemo(() => {
    const allValues = Array.from(periodData.current.litres.values());
    const maxValue = Math.max(...allValues, 1);
    let stepSize = 250;
    if (maxValue <= 100) {
      stepSize = 25;
    } else if (maxValue <= 250) {
      stepSize = 50;
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: comparisonFilterEnabled
          ? {
              textAlign: "center" as const,
              position: "bottom" as const,
              align: "center" as const,
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 12,
                font: { size: 12, lineHeight: 1.2 },
                boxWidth: 10,
                generateLabels: (chart: any) => {
                  const original =
                    ChartJS.defaults.plugins.legend.labels.generateLabels(
                      chart
                    );
                  return original.map((label: any) => ({
                    ...label,
                    text: periodValues[label.text] ?? label.text,
                  }));
                },
              },
            }
          : { display: false },
        tooltip: {
          backgroundColor: colors.background2,
          titleColor: "transparent",
          bodyColor: colors.salesAnalyticsCardLabelText,
          borderColor: colors.greyBorder,
          borderWidth: 1,
          padding: 8,
          displayColors: false,
          callbacks: {
            title: () => "",
            label: (context: any) => {
              const type = types[context.dataIndex];
              const originalData = periodData.current.original.get(type);
              if (originalData) {
                const capacity = extractCapacity(originalData.type);
                const volume = Number(originalData.vol);
                const totalLitres = capacity * volume;
                return `(${capacity} * ${volume}) = ${totalLitres} Litres`;
              }
              return `${context.parsed.y} litres`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { color: colors.greyBorder },
        },
        y: {
          grid: { color: colors.greyBorder, lineWidth: 1, drawBorder: false },
          border: { color: colors.greyBorder },
          ticks: {
            stepSize: stepSize,
            callback: (value: any) => {
              return `${value}`;
            },
          },
          suggestedMin: 0,
          suggestedMax: maxValue,
          beginAtZero: true,
        },
      },
    };
  }, [comparisonFilterEnabled, periodValues, periodData, types]);

  return (
    <StyledSalesAnalyticsCard>
      <SalesAnalyticsCardLabel>{data?.name}</SalesAnalyticsCardLabel>
      <SalesAnalyticsContentBox>
        <SalesAnalyticsOverflowBox>
          <Bar data={chartData} options={options} />
        </SalesAnalyticsOverflowBox>
      </SalesAnalyticsContentBox>
    </StyledSalesAnalyticsCard>
  );
}
